import React from 'react';

import cx from 'classnames';
import { Button } from 'themes/playful/components/Button/Button';
import { Item } from 'themes/playful/components/Item/PlayfulItem';

import InfoIcon from 'components/Icons/Info.svg';

import { useServiceSelection } from 'hooks/page/services/useServiceSelection';
import { TServiceProps } from 'hooks/page/services/useServicesPage';

import { BuyNowPayLaterPromo } from 'routes/invoices/components/BuyNowPayLaterPromo/BuyNowPayLaterPromo';

import s from './PlayfulService.module.css';

export const PlayfulService = ({
  service,
  listView = false,
  disabled = false,
  selected = false,
  selectedOption,
  onClick,
  serviceProvider,
}: TServiceProps) => {
  const {
    displayedPrice,
    toggleNoMatchingProvidersModal,
    onServiceClick,
    depositText,
    durationText,
    optionText,
    renderNoMatchingProviderModal,
    bnplUnitPrice,
    bnplCapabilities,
  } = useServiceSelection({
    service,
    selectedOption,
    disabled,
    onClick,
  });

  const { name, description, image } = service;
  const selectedText = selected ? 'Unselect' : 'Select';

  let popovertarget;
  let popovertargetaction;
  const hasOptions = Boolean(service.options?.length);

  if (hasOptions && !selected) {
    popovertarget = 'service-option-modal';
  }
  if (hasOptions) {
    popovertargetaction = 'show';
  }

  const isInteractive = Boolean(onClick);

  return (
    <>
      {renderNoMatchingProviderModal()}
      <Item
        selected={selected}
        listView={listView}
        disabled={disabled}
        title={name}
        image={image}
        className={s.item}
        button={
          <Button
            disabled={disabled}
            className={cx(selected ? s.buttonInverted : s.button)}
            type="button"
            onClick={onServiceClick}
            title={selectedText}
            popovertarget={popovertarget}
            popovertargetaction={popovertargetaction}
          >
            {selectedText}
          </Button>
        }
        overlay={
          disabled && (
            <Item.Overlay>
              {isInteractive ? (
                <button
                  className={s.disabledMessage}
                  onClick={toggleNoMatchingProvidersModal}
                  type="button"
                >
                  <span>
                    No matching service providers{' '}
                    <i className={s.disabledIcon}>
                      <InfoIcon />
                    </i>
                  </span>
                </button>
              ) : (
                <div className={s.disabledMessage}>
                  <span>
                    No matching service providers{' '}
                    <i className={s.disabledIcon}>
                      <InfoIcon />
                    </i>
                  </span>
                </div>
              )}
            </Item.Overlay>
          )
        }
      >
        {selected && serviceProvider && (
          <p className={s.provider}>with {serviceProvider}</p>
        )}
        {description && <p className={s.summary}>{description}</p>}
        {depositText && <p className={s.deposit}>{depositText}</p>}
        {optionText ? (
          <div data-testid="option" className={s.serviceOption}>
            {optionText}
          </div>
        ) : null}
        <ul className={s.options}>
          <li className={s.option}>
            <b>Price:</b> {displayedPrice}
          </li>
          <li className={s.option}>
            <b>Duration:</b> {durationText}
          </li>
        </ul>
        {bnplUnitPrice ? (
          <div className={s.bnplEligible}>
            <BuyNowPayLaterPromo
              unitAmount={bnplUnitPrice}
              paymentMethodTypes={bnplCapabilities}
            />
          </div>
        ) : null}
      </Item>
    </>
  );
};
